import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import MicCupParticipantRegister from './MicCupParticipantRegister';
import { isLlsGroupType, llsCentresFor } from '../constants/llsCentres';

const TRAVELLER_TYPES = ['Player', 'Coach / Teacher', 'Parent / Guardian', 'Guest'];
const SWIMMING_LEVELS = [
  'Confident in pool',
  'Not confident in pool',
  'Confident in open water',
  'Not confident in open water',
];
const STEPS = ['Traveller Info', 'Medical', 'Passport', 'Disclaimers'];

const BLANK_FORM = {
  travellerType: '',
  llsCentre: '',
  firstName: '', middleNames: '', surname: '',
  gender: '',
  contactPhone: '', contactEmail: '',
  guardianName: '', guardianPhone: '', guardianEmail: '',
  allergies: '', dietaryRequirements: '', conditions: '',
  safetyInfo: '', swimmingLevel: '',
  passportNumber: '', dateOfBirth: '', passportExpiry: '',
  nationality: '', countryOfIssue: '',
  disclaimer1: false, disclaimer2: false, disclaimer3: false,
};

function needsGuardian(type) {
  return type === 'Player' || type === 'Guest';
}

function Label({ children, required, note }) {
  return (
    <label className="block text-[13px] font-medium text-[#374151] mb-1">
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
      {note && <span className="text-[11px] font-normal text-[#9CA3AF] ml-1.5">({note})</span>}
    </label>
  );
}

function Input({ value, onChange, type = 'text', placeholder = '', ...rest }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
      {...rest}
    />
  );
}

function Select({ value, onChange, children }) {
  return (
    <select
      value={value} onChange={onChange}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
    >
      {children}
    </select>
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] resize-none"
    />
  );
}

export default function ParticipantRegister() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [tourInfo, setTourInfo]   = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState('');
  const [step, setStep]           = useState(0);
  const [form, setForm]           = useState(BLANK_FORM);

  useEffect(() => {
    if (!token) { setError('Invalid or missing link.'); setLoading(false); return; }
    getDoc(doc(db, 'tourFormTokens', token)).then((snap) => {
      if (!snap.exists() || snap.data().type !== 'participant_reg') {
        setError('This link is invalid or has expired. Please ask your coach for a new link.'); setLoading(false); return;
      }
      const d = snap.data();
      setTourInfo({ requestId: d.requestId, sport: d.sport || '', destination: d.destination || '', departureDate: d.departureDate || '', returnDate: d.returnDate || '', groupType: d.groupType || '', llsGroup: d.llsGroup || '' });
      setLoading(false);
    }).catch(() => { setError('Failed to load. Please try again.'); setLoading(false); });
  }, [token]);

  // LLS tours: everyone picks their centre from the tour's LLS group.
  const llsCentres = tourInfo && isLlsGroupType(tourInfo.groupType) ? llsCentresFor(tourInfo.llsGroup) : [];

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormError('');
  }

  function validateStep(s) {
    if (s === 0) {
      if (!form.travellerType) return 'Please select your traveller type.';
      if (llsCentres.length && !form.llsCentre) return 'Please select your centre.';
      if (!form.firstName.trim()) return 'First name is required.';
      if (!form.surname.trim()) return 'Surname is required.';
      if (!form.gender) return 'Please select your gender.';
      if (!form.contactPhone.trim()) return 'Contact number is required.';
      if (!form.contactEmail.trim()) return 'Email address is required.';
      if (needsGuardian(form.travellerType)) {
        if (!form.guardianName.trim()) return 'Parent / guardian name is required.';
        if (!form.guardianPhone.trim()) return 'Parent / guardian contact number is required.';
        if (!form.guardianEmail.trim()) return 'Parent / guardian email is required.';
      }
    }
    if (s === 1) {
      if (!form.swimmingLevel) return 'Please select your swimming level.';
    }
    if (s === 2) {
      if (!form.passportNumber.trim()) return 'Passport number is required.';
      if (!form.dateOfBirth) return 'Date of birth is required.';
      if (!form.passportExpiry) return 'Passport expiry date is required.';
      if (!form.nationality.trim()) return 'Nationality is required.';
      if (!form.countryOfIssue.trim()) return 'Country of issue is required.';
    }
    if (s === 3) {
      if (!form.disclaimer1) return 'You must agree to the photo and video consent.';
      if (!form.disclaimer2) return 'You must confirm you have personal travel insurance.';
      if (!form.disclaimer3) return 'You must confirm your swimming level is accurate.';
    }
    return null;
  }

  function goNext() {
    const err = validateStep(step);
    if (err) { setFormError(err); return; }
    setFormError('');
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goBack() {
    setFormError('');
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validateStep(3);
    if (err) { setFormError(err); return; }

    setSaving(true);
    try {
      await addDoc(collection(db, 'requests', tourInfo.requestId, 'participants'), {
        formToken: token,
        travellerType:   form.travellerType,
        ...(llsCentres.length ? { llsGroup: tourInfo.llsGroup, llsCentre: form.llsCentre } : {}),
        firstName:       form.firstName.trim(),
        middleNames:     form.middleNames.trim(),
        surname:         form.surname.trim(),
        gender:          form.gender,
        contactPhone:    form.contactPhone.trim(),
        contactEmail:    form.contactEmail.trim(),
        guardianName:    needsGuardian(form.travellerType) ? form.guardianName.trim() : null,
        guardianPhone:   needsGuardian(form.travellerType) ? form.guardianPhone.trim() : null,
        guardianEmail:   needsGuardian(form.travellerType) ? form.guardianEmail.trim() : null,
        allergies:       form.allergies.trim() || null,
        dietaryRequirements: form.dietaryRequirements.trim() || null,
        conditions:      form.conditions.trim() || null,
        safetyInfo:      form.safetyInfo.trim() || null,
        swimmingLevel:   form.swimmingLevel,
        passportNumber:  form.passportNumber.trim(),
        dateOfBirth:     form.dateOfBirth,
        passportExpiry:  form.passportExpiry,
        nationality:     form.nationality.trim(),
        countryOfIssue:  form.countryOfIssue.trim(),
        disclaimer1:     form.disclaimer1,
        disclaimer2:     form.disclaimer2,
        disclaimer3:     form.disclaimer3,
        submittedAt:     serverTimestamp(),
        createdAt:       serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setFormError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function formatDate(str) {
    if (!str) return '';
    const [y, m, d] = str.split('-');
    return `${d}/${m}/${y}`;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <p className="text-[#9CA3AF] text-[14px]">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <p className="text-[15px] font-semibold text-[#111827] mb-2">Link not valid</p>
          <p className="text-[13px] text-[#6B7280]">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-[#1A7A4A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-[17px] font-bold text-[#111827] mb-2">Registration complete!</p>
          <p className="text-[13px] text-[#6B7280]">
            Your details have been saved. Please let your coach know you have completed the registration form.
          </p>
        </div>
      </div>
    );
  }

  // Case/whitespace-insensitive: the live "Mic Cup" group type option (and
  // older saved tours) predates the "MIC Cup" display-name change.
  // Long Haul tours use the same form (they also get the Group Flights tab).
  if (['mic cup', 'long haul'].includes((tourInfo.groupType || '').trim().toLowerCase())) {
    return <MicCupParticipantRegister tourInfo={tourInfo} token={token} />;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
      <div className="max-w-lg mx-auto">

        {/* Header */}
        {(tourInfo.groupType || '').trim().toUpperCase() === 'LLS' ? (
          <div className="rounded-[12px] mb-6 overflow-hidden relative">
            <img src="/LLS Banner.jpeg" alt="LLS" className="w-full h-auto block" />
            <div className="absolute inset-x-0 bottom-0 pt-20 pb-4 px-5 bg-gradient-to-t from-black/85 via-black/45 to-transparent text-center">
              <p className="text-white font-bold text-[18px]">Participant Registration</p>
              {tourInfo && (
                <p className="text-white/85 text-[12px] mt-0.5">
                  {tourInfo.sport} — {tourInfo.destination}
                  {tourInfo.departureDate && ` · ${formatDate(tourInfo.departureDate)}`}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-[#0D2240] rounded-[12px] p-6 mb-6 text-center">
            <p className="text-white font-bold text-[18px]">MST Sports Tours</p>
            <p className="text-[#93C5FD] text-[13px] mt-1">Participant Registration</p>
            {tourInfo && (
              <p className="text-[#93C5FD] text-[12px] mt-0.5">
                {tourInfo.sport} — {tourInfo.destination}
                {tourInfo.departureDate && ` · ${formatDate(tourInfo.departureDate)}`}
              </p>
            )}
          </div>
        )}

        {/* Step indicator */}
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] px-5 py-4 mb-5">
          <div className="flex items-center justify-between">
            {STEPS.map((label, i) => {
              const done = i < step;
              const current = i === step;
              return (
                <div key={i} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold border-2 ${
                      done    ? 'bg-[#1A4A8A] border-[#1A4A8A] text-white' :
                      current ? 'border-[#1A4A8A] text-[#1A4A8A] bg-[#EFF6FF]' :
                                'border-[#E5E7EB] text-[#9CA3AF] bg-white'
                    }`}>
                      {done ? '✓' : i + 1}
                    </div>
                    <span className={`text-[10px] font-medium mt-1 whitespace-nowrap ${current ? 'text-[#1A4A8A]' : done ? 'text-[#374151]' : 'text-[#9CA3AF]'}`}>
                      {label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-px mx-1 mb-4 ${done ? 'bg-[#1A4A8A]' : 'bg-[#E5E7EB]'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── Step 0: Traveller Info ─────────────────────────────────── */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-4">
                <p className="text-[13px] font-semibold text-[#374151]">Traveller Information</p>

                <div>
                  <Label required>Traveller type</Label>
                  <Select value={form.travellerType} onChange={(e) => set('travellerType', e.target.value)}>
                    <option value="">Select…</option>
                    {TRAVELLER_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </Select>
                </div>

                {llsCentres.length > 0 && (
                  <div>
                    <Label required>Centre</Label>
                    <Select value={form.llsCentre} onChange={(e) => set('llsCentre', e.target.value)}>
                      <option value="">Select your centre…</option>
                      {llsCentres.map((c) => <option key={c}>{c}</option>)}
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label required note="must match passport">First name</Label>
                    <Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
                  </div>
                  <div>
                    <Label note="must match passport, leave blank if none">Middle name(s)</Label>
                    <Input value={form.middleNames} onChange={(e) => set('middleNames', e.target.value)} />
                  </div>
                  <div>
                    <Label required note="must match passport">Surname</Label>
                    <Input value={form.surname} onChange={(e) => set('surname', e.target.value)} />
                  </div>
                </div>

                <div>
                  <Label required>Gender</Label>
                  <Select value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                    <option value="">Select…</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Prefer not to say</option>
                  </Select>
                </div>

                <div>
                  <Label required>Contact phone number</Label>
                  <Input type="tel" value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} placeholder="+44 7700 000000" />
                </div>
                <div>
                  <Label required>Contact email address</Label>
                  <Input type="email" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} placeholder="you@example.com" />
                </div>
              </div>

              {/* Guardian fields — only for Player and Guest */}
              {needsGuardian(form.travellerType) && (
                <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-4">
                  <p className="text-[13px] font-semibold text-[#374151]">Parent / Guardian Details</p>
                  <div>
                    <Label required>Parent / guardian full name</Label>
                    <Input value={form.guardianName} onChange={(e) => set('guardianName', e.target.value)} />
                  </div>
                  <div>
                    <Label required>Parent / guardian contact number</Label>
                    <Input type="tel" value={form.guardianPhone} onChange={(e) => set('guardianPhone', e.target.value)} />
                  </div>
                  <div>
                    <Label required>Parent / guardian email</Label>
                    <Input type="email" value={form.guardianEmail} onChange={(e) => set('guardianEmail', e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 1: Medical ────────────────────────────────────────── */}
          {step === 1 && (
            <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-4">
              <p className="text-[13px] font-semibold text-[#374151]">Medical Information</p>
              <p className="text-[12px] text-[#9CA3AF]">Leave blank if not applicable. All information is kept confidential and used only to ensure your safety on tour.</p>
              <div>
                <Label>Any allergies</Label>
                <Textarea value={form.allergies} onChange={(e) => set('allergies', e.target.value)} placeholder="e.g. Peanuts, penicillin…" />
              </div>
              <div>
                <Label>Dietary requirements</Label>
                <Textarea value={form.dietaryRequirements} onChange={(e) => set('dietaryRequirements', e.target.value)} placeholder="e.g. Vegetarian, Halal, Gluten-free…" />
              </div>
              <div>
                <Label>Medical conditions</Label>
                <Textarea value={form.conditions} onChange={(e) => set('conditions', e.target.value)} placeholder="e.g. Asthma, diabetes, epilepsy…" />
              </div>
              <div>
                <Label>Any additional information needed for safety</Label>
                <Textarea value={form.safetyInfo} onChange={(e) => set('safetyInfo', e.target.value)} placeholder="e.g. Carries an EpiPen, requires daily medication…" />
              </div>
              <div>
                <Label required>Swimming level</Label>
                <Select value={form.swimmingLevel} onChange={(e) => set('swimmingLevel', e.target.value)}>
                  <option value="">Select…</option>
                  {SWIMMING_LEVELS.map((l) => <option key={l}>{l}</option>)}
                </Select>
              </div>
            </div>
          )}

          {/* ── Step 2: Passport ───────────────────────────────────────── */}
          {step === 2 && (
            <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-4">
              <p className="text-[13px] font-semibold text-[#374151]">Passport Information</p>
              <p className="text-[12px] text-[#9CA3AF]">All names must match exactly as they appear on your passport.</p>

              <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-[8px] px-4 py-3 space-y-1">
                <p className="text-[12px] text-[#6B7280]"><strong>Name on passport:</strong> {[form.firstName, form.middleNames, form.surname].filter(Boolean).join(' ')}</p>
              </div>

              <div>
                <Label required>Passport number</Label>
                <Input value={form.passportNumber} onChange={(e) => set('passportNumber', e.target.value)} placeholder="e.g. 123456789" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label required>Date of birth</Label>
                  <Input type="date" value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
                </div>
                <div>
                  <Label required>Passport expiry date</Label>
                  <Input type="date" value={form.passportExpiry} onChange={(e) => set('passportExpiry', e.target.value)} />
                </div>
              </div>
              <div>
                <Label required>Nationality</Label>
                <Input value={form.nationality} onChange={(e) => set('nationality', e.target.value)} placeholder="e.g. British" />
              </div>
              <div>
                <Label required>Country of issue</Label>
                <Input value={form.countryOfIssue} onChange={(e) => set('countryOfIssue', e.target.value)} placeholder="e.g. United Kingdom" />
              </div>
            </div>
          )}

          {/* ── Step 3: Disclaimers ────────────────────────────────────── */}
          {step === 3 && (
            <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-5">
              <p className="text-[13px] font-semibold text-[#374151]">Consent &amp; Disclaimers</p>
              <p className="text-[12px] text-[#9CA3AF]">Please read and agree to each of the following before submitting.</p>

              {[
                {
                  key: 'disclaimer1',
                  title: 'Photo & Video Consent',
                  text: 'I agree that photographs and videos taken during this tour may be used by MST Sports Tours for promotional purposes, including social media, website and marketing materials.',
                },
                {
                  key: 'disclaimer2',
                  title: 'Personal Travel Insurance',
                  text: 'I confirm that I (or my parent/guardian on my behalf) will arrange and hold valid personal travel insurance for the duration of this tour, covering medical expenses, cancellation and personal liability.',
                },
                {
                  key: 'disclaimer3',
                  title: 'Swimming Level Accuracy',
                  text: 'I confirm that the swimming level I provided in the medical section is an accurate reflection of my ability, and I understand this information may be used to manage safety during water-based activities.',
                },
              ].map(({ key, title, text }) => (
                <label key={key} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form[key]}
                    onChange={(e) => set(key, e.target.checked)}
                    className="mt-0.5 w-5 h-5 rounded border-[#D1D5DB] text-[#1A4A8A] flex-shrink-0 cursor-pointer"
                  />
                  <div>
                    <p className="text-[13px] font-semibold text-[#374151] mb-0.5">{title}</p>
                    <p className="text-[12px] text-[#6B7280] leading-relaxed">{text}</p>
                  </div>
                </label>
              ))}
            </div>
          )}

          {formError && (
            <p className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-[8px] px-4 py-3 mt-4">{formError}</p>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-5">
            {step > 0 && (
              <button type="button" onClick={goBack}
                className="flex-1 py-3 border border-[#E5E7EB] text-[#374151] font-semibold text-[14px] rounded-[10px] hover:bg-[#F9FAFB] transition">
                ← Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={goNext}
                className="flex-1 py-3 bg-[#1A4A8A] hover:bg-[#0D2240] text-white font-semibold text-[14px] rounded-[10px] transition">
                Continue →
              </button>
            ) : (
              <button type="submit" disabled={saving}
                className="flex-1 py-3 bg-[#0D2240] hover:bg-[#1A4A8A] text-white font-semibold text-[14px] rounded-[10px] transition disabled:opacity-60">
                {saving ? 'Submitting…' : 'Submit Registration'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
